/**
 * Words for the process ledger events that need more than a label: the two
 * that carry an AI's hand (step-model E3, spec §5.4) — `process_ai_proposed`
 * and a `process_step_completed` with `via: ai` or `accepted_proposal` — and
 * `process_step_reopened` (E5, spec 2026-09-20 §6.3), whose detail is the
 * reason the person gave, and `process_reinstated` (SA-770), whose detail is
 * the step, and `process_revision_applied` (E2 D5, SA-934), whose detail is
 * the revision number, the step the process resumed at and how many later
 * values were kept and marked stale. Payload keys are the core's
 * (process_runtime/core.py), snake_case as stored. Returns null for anything
 * else so the record timeline keeps its own label. Also
 * `customer_message_received` (email triage, SA-716), the one non-process
 * event worded here, because the record timeline has no other place for an
 * event's words.
 *
 * `reason` is a code on every row written since SA-405 (`drain._propose` maps
 * an exception to `ai_internal_error`), but the ledger is append-only and
 * older rows carry the exception's "TypeName: message". So the guard stays:
 * only a slug is ever shown; anything else reads "the AI call failed" —
 * exception text never reaches staff.
 */
import { formatConfidence } from './proposalText'

export interface ProcessEventText {
  label: string
  detail: string | null
}

// `process_ai_proposed.reason`: the core's REASON_* plus the caller's own
// transport codes (ai_executor.ERROR_*). Unknown codes fall back to the slug.
const REASON_TEXT = new Map<string, string>(Object.entries({
  assisted: 'left for a person to confirm',
  below_threshold: 'below the step’s confidence threshold',
  requires_missing: 'required fields were missing',
  exit_not_allowed: 'that exit cannot be taken automatically',
  invalid_output: 'the AI’s answer could not be used',
  ai_invalid_output: 'the AI’s answer could not be used',
  ai_no_tool_call: 'the AI’s answer could not be used',
  ai_unconfigured: 'no AI key is configured',
  ai_transport_error: 'the AI call failed',
  ai_internal_error: 'the AI call failed',
}))

// `customer_message_received.intent` (email triage, SA-716): the classifier's
// reply-intent codes. The twin of triage_text.INTENT_WORDS — move both together.
// The Inbox Triage panel reads it too (components/inbox/triageText.intentLine).
export const INTENT_TEXT = new Map<string, string>(Object.entries({
  accepted: 'an acceptance',
  countered: 'a counter',
  declined: 'a decline',
  question: 'a question',
  revision: 'a change request',
  info: 'information',
}))

const SLUG = /^[a-z][a-z0-9_]*$/

function reasonText(reason: string): string {
  const known = REASON_TEXT.get(reason)
  if (known) return known
  return SLUG.test(reason) ? reason.replace(/_/g, ' ') : 'the AI call failed'
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

function stepName(payload: Record<string, unknown>): string | null {
  const step = str(payload.step)
  if (!step) return null
  const words = step.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function sentence(payload: Record<string, unknown>, text: string): string {
  const step = stepName(payload)
  return step ? `${step}: ${text}` : text.charAt(0).toUpperCase() + text.slice(1)
}

function withConfidence(text: string, confidence: unknown): string {
  const pct = formatConfidence(confidence)
  return pct ? `${text} · ${pct}` : text
}

export function describeProcessEvent(
  eventType: string, payload: Record<string, unknown> | null,
): ProcessEventText | null {
  if (eventType === 'process_ai_proposed') {
    if (!payload) return { label: 'AI suggestion', detail: null }
    const exit = str(payload.exit)
    if (payload.applied === true && exit) {
      return { label: 'AI suggestion', detail: sentence(payload, withConfidence(`applied “${exit}”`, payload.confidence)) }
    }
    const reason = str(payload.reason)
    const why = reason ? ` — ${reasonText(reason)}` : ''
    // A vetted proposal always carries a confidence; its exit may be null
    // (fields only) — the task page says "field values" for the same case.
    const vetted = formatConfidence(payload.confidence) !== null
    const what = exit || vetted
      ? withConfidence(exit ? `suggested “${exit}”` : 'suggested field values', payload.confidence)
      : 'no suggestion'
    return { label: 'AI suggestion', detail: sentence(payload, `${what}${why}`) }
  }
  if (eventType === 'process_step_reopened') {
    // E5: payload {step, from_step, task_id, reason, stale_fields}. The
    // reason is staff-authored text: it is returned as a plain string and the
    // timeline renders it as a text node — never as markup, never trimmed or
    // re-cased (so not through `sentence`).
    if (!payload) return { label: 'Step re-opened', detail: null }
    const step = stepName(payload)
    const reason = str(payload.reason)
    const detail = step && reason ? `${step}: ${reason}` : reason ?? step
    return { label: 'Step re-opened', detail }
  }
  if (eventType === 'process_revision_applied') {
    // E2 D5 (SA-401 D31 on the staff side): payload {revision_number,
    // resumed_at, stale_fields}. The step is the id the process resumed at
    // — `resumed_at`, not `step` — and the stale list is ids only; the
    // detail counts them and never names one.
    if (!payload) return { label: 'Process revised', detail: null }
    const n = typeof payload.revision_number === 'number' ? payload.revision_number : null
    // SA-1044: a revision that stayed carries `stayed_at` and no `resumed_at`;
    // rows written before it carry neither `stayed_at` nor a stay, and read as before.
    if (typeof payload.resumed_at !== 'string' && typeof payload.stayed_at === 'string') {
      const at = stepName({ step: payload.stayed_at })
      if (at) return { label: 'Process revised', detail: n !== null ? `Revision ${n}: the process stayed at ${at}` : `The process stayed at ${at}` }
    }
    const step = stepName({ step: payload.resumed_at })
    const stale = Array.isArray(payload.stale_fields) ? payload.stale_fields.length : 0
    const parts: string[] = []
    if (step) parts.push(`re-opened at ${step}`)
    if (stale > 0) parts.push(`${stale} later ${stale === 1 ? 'value' : 'values'} kept and marked stale`)
    const tail = parts.join('; ')
    const detail = n !== null
      ? (tail ? `Revision ${n}: ${tail}` : `Revision ${n}`)
      : (tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : null)
    return { label: 'Process revised', detail }
  }
  if (eventType === 'process_reinstated') {
    // SA-770: payload {step}. A record rejected as not an enquiry was
    // reinstated; its process went back to the step it was closed at.
    return { label: 'Process reinstated', detail: payload ? stepName(payload) : null }
  }
  if (eventType === 'process_step_completed') {
    // One label for one event type; only an AI's hand adds a detail line.
    if (!payload) return { label: 'Step completed', detail: null }
    const exit = str(payload.exit)
    const head = exit ? `“${exit}” — ` : ''
    if (payload.via === 'ai') {
      return { label: 'Step completed', detail: sentence(payload, withConfidence(`${head}completed by AI`, payload.confidence)) }
    }
    if (typeof payload.accepted_proposal === 'boolean') {
      return { label: 'Step completed', detail: sentence(payload, `${head}AI suggestion ${payload.accepted_proposal ? 'kept' : 'changed'}`) }
    }
    return { label: 'Step completed', detail: null }
  }
  if (eventType === 'customer_message_received') {
    // Email triage (spec T16): a customer's email landed on this record's
    // thread. SA-946 (SA-945 S4): or a WhatsApp group message quoting the
    // record's chain, whose payload names the channel and who posted. The
    // payload is ids, codes and that name; the message itself is in the Inbox.
    const intent = payload ? str(payload.intent) : null
    const words = intent ? INTENT_TEXT.get(intent) : undefined
    const reads = words ? `Reads as ${words}` : null
    if (payload && payload.channel === 'whatsapp-group') {
      const name = str(payload.from_name)
      return { label: 'Group message received', detail: [name ? `from ${name}` : null, reads].filter(Boolean).join(' · ') || null }
    }
    return { label: 'Customer email received', detail: reads }
  }
  if (eventType === 'customer_update_applied') {
    // SA-1029 (spec §D25): ids and counts only; the fields are named by id in the payload.
    const fields = Array.isArray(payload?.fields) ? payload.fields.length : 0
    // SA-1042 (spec §D9): "as suggested" when the person took the proposal as it stood.
    const revision = typeof payload?.revision_number === 'number' ? ` · revision ${payload.revision_number}` : ''
    const suggested = payload?.accepted_proposal === true ? ' · as suggested' : ''
    return { label: 'Customer update applied', detail: `${fields} field${fields === 1 ? '' : 's'}${revision}${suggested}` }
  }
  if (eventType === 'customer_update_dismissed') {
    // SA-1047: a dismissed reply that read as an answer to the quote is a response, not a change request.
    const intent = payload ? str(payload.intent) : null
    const isResponse = intent !== null && ['accepted', 'countered', 'declined'].includes(intent)
    return { label: isResponse ? 'Customer response dismissed' : 'Customer update dismissed', detail: null }
  }
  return null
}
